import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import {
  ArrowLeft,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader2,
  MessageSquare,
  TrendingUp,
  Link2,
  ExternalLink,
  MousePointerClick,
  Globe,
  Monitor,
  Share2,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
  scheduled: { color: 'bg-blue-100 text-blue-700', label: 'Scheduled' },
  running: { color: 'bg-yellow-100 text-yellow-700', label: 'Sending...' },
  paused: { color: 'bg-orange-100 text-orange-700', label: 'Paused' },
  completed: { color: 'bg-green-100 text-green-700', label: 'Completed' },
  failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
};

const smsStatusBadge = {
  queued: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  undelivered: 'bg-orange-100 text-orange-700',
};

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useTenant();

  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [linkStats, setLinkStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // SMS Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch campaign + analytics in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

        const [campaignRes, analyticsRes, linksRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/campaigns/${id}`, { headers }),
          fetch(`${BACKEND_URL}/api/analytics/campaign/${id}`, { headers }),
          fetch(`${BACKEND_URL}/api/links?campaign_id=${id}`, { headers }),
        ]);

        if (!campaignRes.ok) throw new Error('Campaign not found');
        if (cancelled) return;

        const campaignData = await campaignRes.json();
        setCampaign(campaignData);

        if (analyticsRes.ok) {
          const analyticsData = await analyticsRes.json();
          if (!cancelled) setAnalytics(analyticsData);
        }

        if (linksRes.ok) {
          const linksData = await linksRes.json();
          if (!cancelled) setLinkStats(linksData);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, getToken]);

  // Fetch SMS logs
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        campaign_id: id,
        page: logsPage.toString(),
        page_size: '25',
      });
      const res = await fetch(`${BACKEND_URL}/api/sms-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setLogsTotal(data.total || 0);
        setLogsTotalPages(Math.ceil((data.total || 0) / 25));
      }
    } catch {
      // Silently fail — logs are supplementary
    } finally {
      setLogsLoading(false);
    }
  }, [id, logsPage, getToken]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 lg:p-8">
        <button onClick={() => navigate('/dashboard/campaigns')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Campaigns
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const stats = analytics?.stats || {};
  const failureReasons = analytics?.failure_reasons || [];
  const cfg = statusConfig[campaign.status] || statusConfig.draft;
  const deliveryRate = stats.delivery_rate ?? (
    campaign.total_recipients > 0
      ? Math.round((campaign.delivered_count / campaign.total_recipients) * 100)
      : 0
  );

  return (
    <div className="p-6 lg:p-8" data-testid="campaign-detail-page">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate('/dashboard/campaigns')}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 text-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Campaigns
      </button>

      {/* Campaign Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
          {cfg.label}
        </span>
        {campaign.created_at && (
          <span className="text-sm text-gray-400">
            Created {new Date(campaign.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-2 ${linkStats?.links?.length > 0 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-4 mb-6`}>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-medium text-gray-500">Recipients</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{campaign.total_recipients}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-gray-500">Delivered</span>
          </div>
          <div className="text-2xl font-bold text-green-700">{stats.delivered ?? campaign.delivered_count}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-gray-500">Failed</span>
          </div>
          <div className="text-2xl font-bold text-red-700">{stats.failed ?? campaign.failed_count}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-medium text-gray-500">Delivery Rate</span>
          </div>
          <div className="text-2xl font-bold text-purple-700">{deliveryRate}%</div>
        </div>
        {linkStats?.links?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-1">
              <MousePointerClick className="w-5 h-5 text-indigo-500" />
              <span className="text-sm font-medium text-gray-500">Link Clicks</span>
            </div>
            <div className="text-2xl font-bold text-indigo-700">{linkStats.total_clicks}</div>
            {linkStats.total_unique_clicks > 0 && (
              <div className="text-xs text-gray-400 mt-0.5">{linkStats.total_unique_clicks} unique</div>
            )}
          </div>
        )}
      </div>

      {/* Short Links Table + Analytics */}
      {linkStats?.links?.length > 0 && (
        <>
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
            <h2 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
              <Link2 className="w-4 h-4" /> Tracked Links
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 pr-4 font-medium text-gray-500">Short URL</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-500">Destination</th>
                    <th className="text-right py-2 pr-4 font-medium text-gray-500 w-20">Clicks</th>
                    <th className="text-right py-2 font-medium text-gray-500 w-20">Unique</th>
                  </tr>
                </thead>
                <tbody>
                  {linkStats.links.map((link) => (
                    <tr key={link.id} className="border-b border-gray-50">
                      <td className="py-2 pr-4">
                        <a href={link.short_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          {link.short_url.replace(/^https?:\/\//, '')}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="py-2 pr-4 text-gray-600 truncate max-w-[200px]" title={link.original_url}>
                        {link.original_url}
                      </td>
                      <td className="py-2 pr-4 text-right font-medium text-indigo-600">{link.click_count}</td>
                      <td className="py-2 text-right text-gray-500">{link.unique_clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Click Analytics Breakdown */}
          {(linkStats.top_countries?.length > 0 || linkStats.top_browsers?.length > 0 || linkStats.top_referrers?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {/* Top Countries */}
              {linkStats.top_countries?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1.5">
                    <Globe className="w-4 h-4" /> Top Countries
                  </h3>
                  <div className="space-y-1.5">
                    {linkStats.top_countries.slice(0, 5).map((c, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-700 truncate">{c.name}</span>
                        <span className="font-medium text-gray-900 ml-2">{c.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Browsers */}
              {linkStats.top_browsers?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1.5">
                    <Monitor className="w-4 h-4" /> Top Browsers
                  </h3>
                  <div className="space-y-1.5">
                    {linkStats.top_browsers.slice(0, 5).map((b, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-700 truncate">{b.name}</span>
                        <span className="font-medium text-gray-900 ml-2">{b.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Referrers */}
              {linkStats.top_referrers?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1.5">
                    <Share2 className="w-4 h-4" /> Top Referrers
                  </h3>
                  <div className="space-y-1.5">
                    {linkStats.top_referrers.slice(0, 5).map((r, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-700 truncate">{r.name}</span>
                        <span className="font-medium text-gray-900 ml-2">{r.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Message Template */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
        <h2 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Message Template
        </h2>
        <div className="bg-gray-50 rounded-lg p-4 text-gray-800 whitespace-pre-wrap">
          {campaign.message_template}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
        <h2 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Timeline
        </h2>
        <div className="space-y-3">
          {[
            { label: 'Created', time: campaign.created_at },
            { label: 'Started', time: campaign.started_at },
            { label: 'Completed', time: campaign.completed_at },
          ].filter(t => t.time).map(({ label, time }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-orange-400" />
              <span className="text-sm font-medium text-gray-700 w-24">{label}</span>
              <span className="text-sm text-gray-500">
                {new Date(time).toLocaleString('en-AU', {
                  day: 'numeric', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Failure Reasons */}
      {failureReasons.length > 0 && (
        <div className="bg-white rounded-xl border border-red-100 p-6 mb-6">
          <h2 className="text-sm font-medium text-red-600 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Failure Reasons
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Reason</th>
                  <th className="text-right py-2 font-medium text-gray-500 w-20">Count</th>
                </tr>
              </thead>
              <tbody>
                {failureReasons.map((fr, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 pr-4 text-gray-700">{fr.reason}</td>
                    <td className="py-2 text-right font-medium text-red-600">{fr.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SMS Delivery Log */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-gray-500 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> SMS Delivery Log
          </h2>
          <span className="text-xs text-gray-400">{logsTotal} messages</span>
        </div>

        {logsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No SMS logs found for this campaign.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 pr-4 font-medium text-gray-500">Recipient</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-500">Status</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-500 hidden sm:table-cell">Message</th>
                    <th className="text-left py-2 font-medium text-gray-500 hidden md:table-cell">Sent</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const isExpanded = expandedRow === log.id;
                    return (
                      <tr key={log.id} className="group">
                        <td colSpan={5} className="p-0">
                          <div
                            className="flex items-center py-2.5 border-b border-gray-50 cursor-pointer hover:bg-gray-50 px-1 rounded"
                            onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                          >
                            <div className="flex-1 min-w-0 pr-4">
                              <div className="font-medium text-gray-900 truncate">
                                {log.customer_name || log.to_phone}
                              </div>
                              {log.customer_name && (
                                <div className="text-xs text-gray-400">{log.to_phone}</div>
                              )}
                            </div>
                            <div className="pr-4">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${smsStatusBadge[log.status] || 'bg-gray-100 text-gray-600'}`}>
                                {log.status}
                              </span>
                            </div>
                            <div className="hidden sm:block flex-1 min-w-0 pr-4">
                              <p className="text-gray-500 truncate text-xs">{log.message_body}</p>
                            </div>
                            <div className="hidden md:block pr-4 text-xs text-gray-400 whitespace-nowrap">
                              {log.sent_at
                                ? new Date(log.sent_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                                : '—'}
                            </div>
                            <div className="w-6 flex-shrink-0 text-gray-300">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          </div>
                          {/* Expanded detail */}
                          {isExpanded && (
                            <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 space-y-2">
                              <div>
                                <span className="text-xs font-medium text-gray-500">Full Message</span>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{log.message_body}</p>
                              </div>
                              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                                <span>Segments: <strong className="text-gray-700">{log.segments ?? 1}</strong></span>
                                <span>Provider: <strong className="text-gray-700">{log.provider || '—'}</strong></span>
                                {log.delivered_at && (
                                  <span>Delivered: <strong className="text-gray-700">
                                    {new Date(log.delivered_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </strong></span>
                                )}
                                {log.failure_reason && (
                                  <span className="text-red-600">Failure: <strong>{log.failure_reason}</strong></span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Log Pagination */}
            {logsTotalPages > 1 && (
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400">Page {logsPage} of {logsTotalPages}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                    disabled={logsPage === 1}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setLogsPage(p => Math.min(logsTotalPages, p + 1))}
                    disabled={logsPage === logsTotalPages}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
